<template>
  <div id="DmhConfirm">
    <p
      class="brand-text dmh-confirm-text"
      :class="[isWhiteSpacePreWrap ? 'pre-wrap' : 'custom-space']"
      v-if="question.question_type === 'text'"
      v-html="question.sub_text"
    ></p>
    <img src="/img/dmh/map.png" class="img-fluid" v-else />
    <div v-if="isLiveChatLoading" class="live-chat-loader">
      <b-spinner class="brand-primary"></b-spinner>
    </div>
    <p v-if="liveChatError" class="live-chat-error" role="alert">
      Sorry, live chat isn't available right now. Please try again later.
    </p>
  </div>
</template>

<script>
import {
  isWebchatEnabled,
  openWebchat,
  webchatSignOutCount
} from "@/helpers/webchat";

export default {
  name: "DmhConfirm",
  props: ["question", "setAnswer"],
  data() {
    return {
      selectedOptionIndex: null,
      isChecked: false,
      checkbox: null,
      isLiveChatLoading: false,
      liveChatError: false,
      liveChatButtonText: "Open live chat"
    };
  },
  computed: {
    isWhiteSpacePreWrap() {
      return ["Informed Consent", "How we can best support you"].includes(
        this.question.sub_heading
      );
    },
    isLiveChatShown() {
      return this.question?.sub_text.includes("live_button");
    }
  },
  mounted() {
    if (this.isLiveChatShown) {
      // Warm the cached flag check so the click doesn't wait on it.
      isWebchatEnabled(this.$store.getters.user_id);
      this.appendLiveButton();
    }
    this.handleCheckbox();
  },
  watch: {
    question() {
      this.resetCheckbox();

      this.$nextTick(() => {
        this.handleCheckbox();
      });
    }
  },
  methods: {
    decodeData(data) {
      try {
        return atob(data);
      } catch (error) {
        return data;
      }
    },
    appendLiveButton() {
      const liveButtonDiv = document.querySelector(".live_button");

      if (liveButtonDiv) {
        const p = document.createElement("p");
        p.className = "additional-text";
        p.textContent = "Or speak to a specialist now";
        liveButtonDiv.appendChild(p);

        const button = document.createElement("button");
        button.className = "btn-open-live-chat";
        button.textContent = this.liveChatButtonText;
        liveButtonDiv.appendChild(button);

        button.addEventListener("click", () => {
          this.initChat();
        });
      }
    },
    async initChat() {
      if (this.isLiveChatLoading) return;
      this.liveChatError = false;
      this.isLiveChatLoading = true;
      const token = this.$store.getters.user_id;
      const signOutsBefore = webchatSignOutCount();
      const useWebchat = await isWebchatEnabled(token);
      // Logged out while the flag check was in flight: open nothing for the old user.
      if (
        webchatSignOutCount() !== signOutsBefore ||
        !this.$store.state.user.authenticated
      ) {
        this.isLiveChatLoading = false;
        return;
      }
      if (!useWebchat) {
        this.isLiveChatLoading = false;
        this.initMessageBirdChat();
        return;
      }
      try {
        await openWebchat(token);
      } catch (e) {
        // Already reported to Sentry by the helper; the page carries on without chat.
        this.liveChatError = true;
      } finally {
        this.isLiveChatLoading = false;
      }
    },
    initMessageBirdChat() {
      const sf = document.getElementById("live-chat-widget");

      if (sf) {
        sf.style.display = "initial";
        window?.MessageBirdChatWidget?.toggleChat();
      } else {
        this.isLiveChatLoading = true;
        const script = document.createElement("script");
        script.src = "/assets/salesforce_Script.js";
        script.type = "text/javascript";
        script.charset = "UTF-8";
        script.setAttribute("id", "sf-script");

        script.onload = () => {
          if (window?.MessageBirdChatWidget) {
            window.MessageBirdChatWidget.on("ready", () => {
              window.MessageBirdChatWidget.identify(
                this.$store.getters.user_id
              );
              window.MessageBirdChatWidget.toggleChat();
              this.isLiveChatLoading = false;
            });
          }
        };

        document.head.appendChild(script);
      }
    },
    selectOptionIndex(i) {
      this.setAnswer(i);
      this.selectedOptionIndex = i;
    },
    openPrivacyPolicy() {
      const baseUrl = window.location.origin;
      window.open(baseUrl + "/privacy-policy", "_blank").focus();
    },
    handleCheckbox() {
      this.checkbox = document.getElementById("agreementCheckbox");
      if (!this.checkbox) {
        this.setAnswer(true);
      } else {
        this.checkbox.addEventListener("change", () => {
          this.updateCheckboxState();
        });
        this.checkbox.addEventListener("keydown", e => {
          if (e.key === "Enter") {
            this.checkbox.checked = !this.checkbox.checked;
            this.updateCheckboxState();
          }
        });
      }
    },
    updateCheckboxState() {
      this.isChecked = this.checkbox.checked;
      this.setAnswer(this.isChecked ? true : null);
    },
    resetCheckbox() {
      this.setAnswer(null);
      this.isChecked = false;
      this.checkbox = null;
    }
  }
};
</script>
<style lang="less">
#DmhConfirm {
  display: flex;
  flex-direction: column;
  align-items: center;
  #agreementCheckbox {
    cursor: pointer;
    width: 14px;
    height: 14px;
    margin-right: 4px;
  }
  .privacy-text {
    font-family: "PlusJakartaSansBold";
  }
  .privacy-link {
    font-family: "PlusJakartaSansBold";
    text-decoration: underline;
  }
  p {
    text-align: left;
    width: 100%;
    font-size: 1.6rem;
  }
  small {
    font-size: 1.5rem;
  }
  img {
    max-width: 600px;
    border-radius: 16px;
    cursor: not-allowed;
  }
  .dmh-confirm-text {
    font-family: "PlusJakartaSansRegular";
    color: #1e1e22;

    h3 {
      font-size: 2rem;
      padding: 0;
      letter-spacing: 0;
      margin-bottom: 0;
    }
    ul,
    p {
      margin-bottom: 0;
    }
    h3,
    ul,
    li,
    p {
      color: #1e1e22;
    }
    b {
      font-family: "PlusJakartaSansBold";
    }
    section {
      border: none !important;
      padding: 0 !important;
    }
  }
  .pre-wrap {
    white-space: pre-wrap;

    p {
      white-space: pre-wrap;
      margin-bottom: 12px;
    }
  }
  .custom-space {
    ul,
    ol {
      &:not(:first-child) {
        padding-left: 20px;
        margin-top: 20px;
      }
    }
    h3:not(:first-child) {
      margin-top: 24px;
    }
    li {
      margin-top: 16px;
    }
    p:not(:first-child),
    section,
    .phone_numbers {
      margin-top: 20px;
    }
  }

  .phone_numbers {
    display: flex;
    flex-direction: column;
    gap: 16px;
    border: 1px solid #e0e5f5;
    padding: 24px;
    border-radius: 8px;
    margin-bottom: 32px;

    & > div div {
      display: flex;
      justify-content: space-between;
      &:not(:last-child) {
        margin-bottom: 16px;
      }
    }

    span {
      font-family: "PlusJakartaSansMedium";
      color: #3c16f9;
      text-decoration: underline;
    }

    .additional-text {
      text-align: center;
    }
  }

  .live_button {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 32px;
    border-top: 1px solid #e0e5f5;
  }

  .btn-open-live-chat {
    width: 100%;
    border-radius: 40px;
    border: 0;
    color: #1e1e22;
    font-size: 1.4rem;
    font-family: "PlusJakartaSansMedium";
    background-color: rgba(30, 30, 34, 0.05);
    padding: 15px;
  }

  .live-chat-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
  }

  .live-chat-error {
    margin-top: 20px;
    text-align: center;
    font-size: 1.4rem;
  }
}
</style>
